<template>
  <div id="pasta-now-address-search" class="relative h-[90vh]">
    <PastaNowAddressSearchBackgroundImage
      v-if="largerOrEqualLg"
      src="https://cdn.shopify.com/s/files/1/0566/2602/0532/files/address-lookup-pasta-evangelists_m-1.webp?v=1753364983"
    />
    <div
      v-if="isStep1"
      id="address-lookup-container"
      class="relative w-full lg:flex lg:h-full lg:justify-center"
    >
      <div
        class="flex flex-col lg:flex-row lg:items-center justify-center w-full lg:max-w-[650px] lg:mt-[5vh] 2xl:mt-[100px] h-fit bg-sandstone rounded-sm lg:border border-sienna-muted lg:border-solid"
      >
        <div
          v-if="!isLoading"
          class="lg:min-h-[220px] p-6 md:py-16 md:px-[154px] lg:py-[6vh] lg:px-12"
        >
          <PastaNowAddressSearchHeader />
          <GeocodingTypeahead
            ref="addressSearchInput"
            class="z-20 w-full bg-light-linen max-w-[460px] lg:max-w-[552px] mx-auto"
            @suggestion-press="handleFuzzySearchPressed"
            @focus="scrollToTitle"
          />
          <p
            v-if="voucherInfo?.attributes.textSection3"
            class="mt-8 text-xs text-center text-brown-grey"
          >
            {{ voucherInfo?.attributes?.textSection3 }}
          </p>
        </div>
        <div
          v-else
          class="min-h-[200px] p-6 flex flex-col items-center justify-center md:px-8 md:py-16"
        >
          <BaseLoadingIcon class="size-16 text-terracota-dark" />
          <p
            class="text-3xl text-center md:text-5xl md:short:text-[38px] text-terracota-dark font-brand"
          >
            Checking availability in your area
          </p>
        </div>
      </div>
    </div>
    <div v-else class="flex flex-col justify-between w-full bg-sandstone md:p-0">
      <div
        class="bg-sandstone lg:z-50 p-4 sm:p-12 md:p-6 space-y-6 sm:space-y-8 md:space-y-12 lg:mt-[5vh] 2xl:mt-[100px] lg:mx-auto lg:max-w-8xl lg:rounded-sm lg:border lg:border-sienna-muted"
      >
        <h2 class="text-4xl text-center lg:text-5xl text-terracota-dark font-brand">
          Find
          <i>Pasta Evangelists</i>
          in your area
        </h2>
        <div class="flex justify-center w-full">
          <button
            v-if="!isInputOpen"
            id="address-lookup-change-address-btn"
            class="flex justify-between py-3 pr-3 2xl:py-5 2xl:pr-5 w-full bg-light-linen md:max-w-xl md:w-[50vw] border border-sienna-muted h-14 items-center"
            @click="toggleInputOpen"
          >
            <div class="flex items-center flex-1 overflow-hidden text-left truncate">
              <LocationMarkerIcon
                class="mx-3 size-6 text-terracota-dark fill-sienna-muted shrink-0"
              />
              {{ displayAddress }}
            </div>
            <span
              id="address-lookup-change-address-span"
              class="ml-2 text-xs underline text-off-black"
            >
              Change
            </span>
          </button>
          <PastaNowAddressSearchComponent
            v-else
            ref="addressSearchInput"
            class="flex justify-between w-full bg-light-linen md:max-w-xl md:w-[50vw]"
            @press="handleSuggestion"
            @focus="scrollToTitle"
            @suggestion-press="isInputOpen = false"
          />
        </div>
        <div class="grid grid-cols-2 gap-4 mx-auto md:gap-y-0 md:grid-cols-4 md:gap-x-4">
          <PastaTypeBlock
            label="Dine in"
            type="eat-in"
            :text="eatInText"
            :btn-text="eatInBtnText"
            @clicked="goToEatInRestaurant"
          />
          <PastaTypeBlock
            label="Collect"
            type="pickup"
            :text="pickupText"
            :btn-text="pickupnBtnText"
            :is-disabled="!isEligibleForPickup"
            @clicked="goToPickup"
          />
          <PastaTypeBlock
            label="Delivery"
            type="delivery"
            :text="takeawayText"
            :btn-text="takeawayBtnText"
            :is-disabled="!isEligibleForPastaNow"
            :is-loading="isPastaNowLoading"
            @clicked="goToPastaNow"
          />
          <PastaTypeBlock
            label="Cook at home"
            type="recipe-kits"
            :text="recipeKitsText"
            :btn-text="recipeKitsBtnText"
            :is-loading="updateRecipeKitBasketMutation.isPending.value"
            @clicked="goToRecipeKits"
          />
        </div>
      </div>
    </div>
    <div v-if="!largerOrEqualLg" id="pn-address-search-mobile" class="w-full h-[80vh] relative">
      <PastaNowAddressSearchBackgroundImage
        src="https://cdn.shopify.com/s/files/1/0566/2602/0532/files/address-lookup-pasta-evangelists_m-1.webp?v=1753364983"
      />
    </div>
  </div>
  <UnpausePlanOrOneOffModal v-model:is-open="isUnpausePlanOrOneOffModalOpen" />
</template>

<script setup lang="ts">
  import { trackCustomEvent } from '@/utils/analytics';
  import { ManualAddressInputPayload } from '@/components/BaseAddressInputs.vue';
  import { useRoute, useRouter } from 'vue-router';
  import useAddressFinder from '@/composables/useAddressFinder';
  import dayjs from 'dayjs';
  import { useTitle, breakpointsTailwind, useBreakpoints } from '@vueuse/core';
  import PastaNowAddressSearchComponent from '@/components/PastaNowAddressSearchComponent.vue';
  import { Coordinates } from '@/types';
  import useGetTimeslots from '@/api/queries/pensa/useGetTimeslots';
  import { LocationMarkerIcon } from '@heroicons/vue/solid';
  import useGeoLocationFromAddress from '@/composables/useGeoLocationFromAddress';
  import usePastaNowBasket from '@/stores/pastaNowBasketStore';
  import useCreateBasket from '@/api/mutations/pensa/createBasket';
  import currentCustomerStore from '@/stores/currentCustomerStore';
  import useMarketingObservable from '@/composables/useMarketingObservable';
  import * as Sentry from '@sentry/vue';
  import { convertToShortAddressString } from '@/utils/address';
  import useUpdateRecipeKitBasket from '@/api/mutations/pensa/updateRecipeKitBasket';
  import useBasketStore from '@/stores/basketStore';
  import usePreviousSuggestions from '@/composables/usePreviousSuggestions';
  import PastaNowAddressSearchHeader from './PastaNowAddressSearchHeader.vue';
  import useGetVoucherInfo from '@/api/queries/pensa/useGetVoucherInfo';
  import useSubscriptionStore from '@/stores/subscriptionStore';
  import useScheduleStore from '@/stores/scheduledStore';
  import useGetValidStoresForAllTypes from '@/api/queries/pensa/useGetValidStoresForAllTypes';
  import UnpausePlanOrOneOffModal from '@/components/UnpausePlanOrOneOffModal.vue';
  import useGetEatInAvailability from '@/api/queries/pensa/useGetEatInAvailability';

  const pastaNowBasketStore = usePastaNowBasket();
  const createBasketMutation = useCreateBasket();
  const currentCustomer = currentCustomerStore();
  const subscriptionStore = useSubscriptionStore();
  const scheduleStore = useScheduleStore();
  const breakpoints = useBreakpoints(breakpointsTailwind);
  const { isMounted } = useMarketingObservable();
  const basketStore = useBasketStore();

  useTitle('Enter Your Delivery Address | Pasta Evangelists');

  const { retrieve } = useAddressFinder();
  const { getAproxLocationFromPostcode, getLocationFromAddress } = useGeoLocationFromAddress();
  const router = useRouter();
  const route = useRoute();
  const updateRecipeKitBasketMutation = useUpdateRecipeKitBasket();
  const { savedSuggestions } = usePreviousSuggestions();

  const autoRedirect = route.query.autoredirect;
  const voucherCode = route.query.vc as string;

  const { data: voucherInfo } = useGetVoucherInfo(ref(voucherCode));

  const address = ref<ManualAddressInputPayload>();
  const coordinates = ref<Coordinates | null>(null);
  const suggestionId = ref<string | null>();
  const isLoading = ref(false);
  const isPastaNowLoading = ref(false);
  const isEligibleForPastaNow = ref(false);
  const isInputOpen = ref(false);
  const isUnpausePlanOrOneOffModalOpen = ref(false);
  const title = ref<HTMLElement>();
  const addressSearchInput = ref<InstanceType<typeof PastaNowAddressSearchComponent>>();
  const screenHeight = ref(window.innerHeight);

  const largerOrEqualLg = breakpoints.greaterOrEqual('lg');

  const takeawayText = computed(() => {
    if (!availableTakeawayStore.value) return 'Currently unavailable, but we’re working on it!';
    if (!isEligibleForPastaNow.value) {
      const tomorrow = dayjs().add(1).format('YYYY-MM-DD');
      const nextSlot = availableTakeawayStore.value.attributes.openingHours.find(
        slot =>
          (slot.dayOfWeek === dayjs().day() &&
            dayjs().isBefore(dayjs(`${dayjs().format('YYYY-MM-DD')} ${slot.openingTime}`))) ||
          (slot.dayOfWeek === dayjs().day() + 1 &&
            dayjs().isBefore(dayjs(`${tomorrow} ${slot.openingTime}`)))
      );
      if (nextSlot)
        return `Reopens at ${dayjs(`${tomorrow} ${nextSlot.openingTime}`).format('h:mma')}`;
    }
    if (shouldShowNotOpenWarning.value) return `Delivery in 15-20 mins from ${openingTime.value}`;
    return 'Delivered hot in 15-20 minutes';
  });

  const takeawayBtnText = computed(() => {
    if (!isEligibleForPastaNow.value) return 'Not available';
    return 'Order now';
  });

  const isStep1 = computed(() => !address.value || isLoading.value);

  const nextRecipeKitOrder = computed(() => {
    if (!scheduleStore.activeFutureWeeks) return null;
    const scheduledDates = Object.keys(scheduleStore.activeFutureWeeks).sort(
      (a, b) => +new Date(a) - +new Date(b)
    );
    if (scheduledDates.length) return scheduledDates[0];
    return null;
  });

  const recipeKitsText = computed(() => {
    if (
      subscriptionStore.selectedSubscription?.state === 'paused' ||
      subscriptionStore.selectedSubscription?.state === 'failed'
    )
      return 'Unpause plan or order one-off recipe kits';
    if (subscriptionStore.selectedSubscription?.state === 'active' && nextRecipeKitOrder.value)
      return `Your recipe kit arriving <span class="font-semibold">${dayjs(
        nextRecipeKitOrder.value
      ).format('ddd D MMM')}</span>`;
    return 'Easy recipe kits, next day delivery';
  });

  const recipeKitsBtnText = computed(() => {
    if (subscriptionStore.selectedSubscription?.state === 'active' && nextRecipeKitOrder.value)
      return 'View order';
    if (
      subscriptionStore.selectedSubscription?.state === 'active' &&
      !scheduleStore.activeFutureWeeks
    )
      return 'View plan';
    return 'Order recipe kits';
  });

  const pickupText = computed(() => {
    if (!availablePickupStore.value) return 'Currently unavailable, but we’re working on it!';
    if (!isPickupOpen.value && firstPickupTimeslot.value)
      return `<span class="font-semibold">${
        availablePickupStore.value.attributes.site
      }</span>, reopens at ${dayjs(firstPickupTimeslot.value.startTime).format('h:mma')}`;
    if (!isPickupOpen.value && !lastPickupTimeslot.value) {
      const tomorrow = dayjs().add(1).format('YYYY-MM-DD');
      const nextSlot = availablePickupStore.value.attributes.openingHours.find(
        slot =>
          (slot.dayOfWeek === dayjs().day() &&
            dayjs().isBefore(dayjs(`${dayjs().format('YYYY-MM-DD')} ${slot.openingTime}`))) ||
          (slot.dayOfWeek === dayjs().day() + 1 &&
            dayjs().isBefore(dayjs(`${tomorrow} ${slot.openingTime}`)))
      );

      if (nextSlot)
        return `<span class="font-semibold">${
          availablePickupStore.value.attributes.site
        }</span>, reopens at ${dayjs(`${tomorrow} ${nextSlot.openingTime}`).format('h:mma')}`;
    }
    if (lastPickupTimeslot.value)
      return `<span class="font-semibold">${
        availablePickupStore.value.attributes.site
      }</span>, open until ${dayjs(lastPickupTimeslot.value.endTime).format('h:mma')}`;
    return 'Currently unavailable, but we’re working on it!';
  });

  const pickupnBtnText = computed(() =>
    !isEligibleForPickup.value ? 'Not available' : 'Order now'
  );

  const eatInText = computed(() => {
    if (!availableEatInStore.value) return 'Enjoy fresh pasta at our restaurants';
    if (isEatInOpen.value) {
      const today = dayjs().format('YYYY-MM-DD');
      const currentSlot = availableEatInStore.value.attributes.openingHours.find(
        slot =>
          (slot.dayOfWeek === dayjs().day() &&
            dayjs().isAfter(dayjs(`${today} ${slot.openingTime}`))) ||
          (slot.dayOfWeek === dayjs().day() - 1 &&
            dayjs().isBefore(dayjs(`${today} ${slot.closingTime}`)))
      );

      if (currentSlot)
        return `<span class="font-semibold">${
          availableEatInStore.value.attributes.site
        }</span>, open until ${dayjs(`${today} ${currentSlot?.closingTime}`).format('h:mma')}`;
    } else {
      const tomorrow = dayjs().add(1).format('YYYY-MM-DD');
      const nextSlot = availableEatInStore.value.attributes.openingHours.find(
        slot =>
          (slot.dayOfWeek === dayjs().day() &&
            dayjs().isBefore(dayjs(`${dayjs().format('YYYY-MM-DD')} ${slot.openingTime}`))) ||
          (slot.dayOfWeek === dayjs().day() + 1 &&
            dayjs().isBefore(dayjs(`${tomorrow} ${slot.openingTime}`)))
      );

      if (nextSlot)
        return `<span class="font-semibold">${
          availableEatInStore.value.attributes.site
        }</span>, reopens at ${dayjs(`${tomorrow} ${nextSlot.openingTime}`).format('h:mma')}`;
    }
    return 'Enjoy fresh pasta at our restaurants';
  });

  const eatInBtnText = computed(() =>
    !availableEatInStore.value ? 'View restaurants' : 'View restaurant'
  );

  watch(
    [isLoading, isEligibleForPastaNow, address],
    ([newIsLoading, newIsEligibleForPastaNow, newAddress], [oldIsLoading]) => {
      if (!newIsLoading && newIsEligibleForPastaNow && newAddress) {
        trackCustomEvent('isEligibleForPastaNow');
        return;
      } else if (oldIsLoading && !newIsLoading && !newIsEligibleForPastaNow) {
        trackCustomEvent('isNotEligibleForPastaNow');
      }
    }
  );

  const { data } = useGetValidStoresForAllTypes(coordinates);

  const availableTakeawayStore = computed(() => {
    if (!data.value || !data.value['delivery']?.data.length) return null;
    return data.value['delivery'].data[0];
  });

  const availableTakeawayStoreId = computed(() => {
    if (!availableTakeawayStore.value) return null;
    return availableTakeawayStore.value.id;
  });

  const takeawayTimeslotParams = computed(() => ({
    restaurantId: availableTakeawayStoreId.value,
    fulfillmentType: 'delivery' as const,
  }));

  const takeawayTimeslotsQuery = useGetTimeslots(takeawayTimeslotParams);

  const availablePickupStore = computed(() => {
    if (!data.value || !data.value['pickup']?.data.length) return null;
    return data.value['pickup'].data[0];
  });

  const availablePickupStoreId = computed(() => {
    if (!availablePickupStore.value) return null;
    return availablePickupStore.value.id;
  });

  const pickupTimeslotParams = computed(() => ({
    restaurantId: availablePickupStoreId.value,
    fulfillmentType: 'pickup' as const,
  }));

  const pickupTimeslotsQuery = useGetTimeslots(pickupTimeslotParams);

  const isEligibleForPickup = computed(() => !!pickupTimeslotsQuery.data.value?.length);

  const firstPickupTimeslot = computed(() => {
    if (!pickupTimeslotsQuery.data.value?.length) return null;
    return pickupTimeslotsQuery.data.value[0];
  });

  const lastPickupTimeslot = computed(() => {
    if (!pickupTimeslotsQuery.data.value?.length) return null;
    return pickupTimeslotsQuery.data.value[pickupTimeslotsQuery.data.value.length - 1];
  });

  const isPickupOpen = computed(() => {
    if (!pickupTimeslotsQuery.data.value?.length) return false;
    return dayjs(pickupTimeslotsQuery.data.value[0].startTime).diff(dayjs(), 'minute') < 10;
  });

  const availableEatInStore = computed(() => {
    if (!data.value || !data.value['eatIn']?.data.length) return null;
    return data.value['eatIn'].data[0];
  });

  const availableEatInStoreId = computed(() => {
    if (!availableEatInStore.value) return null;
    return availableEatInStore.value.id;
  });

  const { data: isEatInOpen } = useGetEatInAvailability(availableEatInStoreId);

  const toggleInputOpen = () => {
    isInputOpen.value = !isInputOpen.value;
    if (isInputOpen.value) nextTick(focusInput);
  };

  type HandleSuggestionParams = { id: string } | ManualAddressInputPayload;

  const handleFuzzySearchPressed = async (params: {
    zip: string;
    lat: number | null;
    lon: number | null;
    addressLine2: string | null;
    city: string | null;
    id: string;
  }) => {
    isLoading.value = true;
    suggestionId.value = params.id;

    if (!params.lat || !params.lon) {
      Sentry.withScope(scope => {
        scope.setExtra('postcode', params.zip);
        Sentry.captureException(new Error('Google places failed to get gps coordinates'));
      });
      isLoading.value = false;
      isInputOpen.value = false;
      isEligibleForPastaNow.value = false;
      return;
    }

    coordinates.value = { lat: params.lat.toString(), lon: params.lon.toString() };

    address.value = {
      address1: 'ignore_me',
      address2: params.addressLine2 || '',
      city: params.city || '',
      zip: params.zip,
      region: '',
      regionCode: '',
      company: '',
      country: 'United Kingdom',
      countryCode: 'UK',
    };

    isInputOpen.value = false;
  };

  const handleSuggestion = async (params: HandleSuggestionParams) => {
    isLoading.value = true;
    if ('id' in params) {
      if (params.id === suggestionId.value) return;
      suggestionId.value = params.id;
      const { Items } = await retrieve(params.id);

      // when it doesn't return any results
      if (Items.length === 0) {
        // we don't want to keep saved an address that doesn't exist
        if (savedSuggestions.value?.[params.id]) {
          delete savedSuggestions.value[params.id];
        }
        isLoading.value = false;
        isInputOpen.value = false;
        return;
      }

      const company = Items[0].Company;
      address.value = {
        address1: (company ? `${company}, ` : '') + Items[0].Line1,
        address2: Items[0].Line2 + (Items[0].Line3 ? ', ' + Items[0].Line3 : ''),
        city: Items[0].City,
        zip: Items[0].PostalCode,
        region: Items[0].ProvinceName,
        regionCode: Items[0].ProvinceCode,
        company: Items[0].Company,
        country: Items[0].CountryName,
        countryCode: Items[0].CountryIso2,
      };
      trackCustomEvent('loqate-address-id-fetched', { address_id: params.id });
    } else {
      address.value = { ...params };
    }

    // use postcodes.io to get the aprox lat/lng for postcode
    const coordinatesResult = await getAproxLocationFromPostcode(address.value.zip);

    // if the coordinates we got are the same we have currently saved we don't need to do anything
    if (
      coordinatesResult.data?.latitude &&
      coordinatesResult.data?.longitude &&
      coordinatesResult.data.latitude === coordinates.value?.lat &&
      coordinatesResult.data.longitude === coordinates.value?.lon
    ) {
      isLoading.value = false;
      isInputOpen.value = false;
      return;
    }

    if (coordinatesResult.data) {
      const { latitude: lat, longitude: lon } = coordinatesResult.data;
      coordinates.value = { lat, lon };
    } else {
      // if it fails we log the error and try to get the lat/lng using postcoder
      // which is more reliable but is more expensive
      Sentry.withScope(scope => {
        scope.setExtra('postcode', address.value!.zip);
        Sentry.captureException(new Error('Postcodes.io failed to get coordinates'));
      });
      const coordinatesResult = await getLocationFromAddress(
        convertToShortAddressString(address.value)
      );
      if (coordinatesResult) {
        const { latitude: lat, longitude: lon } = coordinatesResult;
        coordinates.value = { lat, lon };
      } else {
        isLoading.value = false;
        isInputOpen.value = false;
        Sentry.withScope(scope => {
          scope.setExtra('address', convertToShortAddressString(address.value!));
          Sentry.captureException(new Error('Postcoder failed to get gps coordinates'));
        });
      }
    }

    isInputOpen.value = false;
  };

  const shouldShowNotOpenWarning = computed(
    () => firstTimeSlot.value && firstTimeSlot.value.startTime !== 'asap'
  );

  const firstTimeSlot = computed(() => {
    if (!takeawayTimeslotsQuery.data.value?.length) return null;
    const timeslots = [...takeawayTimeslotsQuery.data.value];
    const firstTimeSlot =
      timeslots.find(timeslot => timeslot.startTime === 'asap') ||
      timeslots.sort((a, b) => +new Date(a.startTime) - +new Date(b.startTime))[0];
    if (!firstTimeSlot) return null;
    return firstTimeSlot;
  });

  const openingTime = computed(() => {
    if (!firstTimeSlot.value || firstTimeSlot.value.startTime === 'asap') return '';
    return dayjs(firstTimeSlot.value.startTime).format('HH:mm');
  });

  const menusAvailable = computed(() => {
    if (!availableTakeawayStore.value && !availablePickupStore.value && !availableEatInStore.value)
      return 'Recipe Kits only';
    let menus = 'Recipe Kits';
    if (availableTakeawayStore.value) menus = 'Takeaway + ' + menus;
    if (availablePickupStore.value) menus = 'Pickup + ' + menus;
    if (availableEatInStore.value) menus = 'Eat In + ' + menus;
    return menus;
  });

  watch(
    [data, takeawayTimeslotsQuery.isLoading, firstTimeSlot],
    async ([stores, timeSlotsLoading, timeSlot]) => {
      if (timeSlotsLoading) return;
      if (!address.value) isEligibleForPastaNow.value = false;
      else {
        const storeExistButNoTimeSlots = !timeSlot;
        const noStoresAvailable = stores?.['delivery'] && !stores['delivery'].data.length;

        if (storeExistButNoTimeSlots || noStoresAvailable) {
          isEligibleForPastaNow.value = false;
          if (autoRedirect === 'rk') {
            trackCustomEvent('address_looked_up', {
              postcode_searched: address.value?.zip,
              menus_available: menusAvailable.value,
            });

            await basketStore.updateAddressWhenBasketAvailable(address.value);
            router.push({ name: 'product-box', query: { ...route.query, dtw: 'true' } });
            return;
          }
        } else {
          isEligibleForPastaNow.value = true;

          if (autoRedirect === 'rk') {
            router.push({ query: undefined });
          }
        }
      }

      trackCustomEvent('address_looked_up', {
        postcode_searched: address.value?.zip,
        menus_available: menusAvailable.value,
      });

      if (autoRedirect === 'tw' && isEligibleForPastaNow.value) {
        await goToPastaNow();
        return;
      }

      isLoading.value = false;
    }
  );

  const focusInput = () => {
    addressSearchInput.value?.addressInput?.focus();
  };

  const goToPastaNow = async () => {
    trackCustomEvent('CTA_clicked', {
      click_id: 'addresslookup_proposition_TW',
      click_text: 'ORDER NOW',
      click_url: window.location.href,
    });

    if (!address.value) {
      focusInput();
      return;
    }

    if (!firstTimeSlot.value) {
      throw new Error('Tried to go to takeaway menu without a timeslot');
    }

    if (!availableTakeawayStore.value?.id) {
      throw new Error('Tried to go to takeaway menu without a store');
    }

    isPastaNowLoading.value = true;
    basketStore.updateAddressWhenBasketAvailable(address.value);

    // clear pasta now basket
    try {
      await pastaNowBasketStore.clearBasket();
    } catch (e) {
      throw new Error('Unable to delete takeaway basket');
    }

    const fulfillmentDate = firstTimeSlot.value.endTime;

    const formattedFulfillmentDate =
      fulfillmentDate === 'asap'
        ? dayjs().format('YYYY-MM-DD')
        : dayjs(fulfillmentDate).format('YYYY-MM-DD');

    try {
      await createBasketMutation.mutateAsync({
        deliveryDate: formattedFulfillmentDate,
        fulfillmentType: 'delivery',
        restaurantId: availableTakeawayStore.value.id,
        timeSlot: firstTimeSlot.value.startTime,
        type: 'TakeawayBasket',
        address: {
          address1: address.value.address1,
          address2: address.value.address2,
          city: address.value.city,
          zip: address.value.zip,
          firstName: currentCustomer.firstName,
          lastName: currentCustomer.lastName,
          lat: coordinates.value?.lat || '',
          lon: coordinates.value?.lon || '',
          phone: '',
          company: address.value.company,
          country: address.value.country,
          countryCode: address.value.countryCode || 'UK',
          region: address.value.region,
          regionCode: address.value.regionCode,
        },
      });
    } catch (e) {
      throw new Error('Unable to create takeaway basket');
    }

    trackCustomEvent('location_assigned', {
      location_assigned: availableTakeawayStore.value.attributes.site,
    });
    trackCustomEvent('address-lookup-pasta-now-click');

    basketStore.updateAddressWhenBasketAvailable(address.value);

    router.push({ name: 'pasta-now', query: { vc: voucherCode } });
    isPastaNowLoading.value = false;
  };

  const goToRecipeKits = async () => {
    if (
      subscriptionStore.selectedSubscription?.state === 'paused' ||
      subscriptionStore.selectedSubscription?.state === 'failed'
    ) {
      isUnpausePlanOrOneOffModalOpen.value = true;
      return;
    }
    if (subscriptionStore.selectedSubscription?.state === 'active' && nextRecipeKitOrder.value) {
      router.push({ name: 'upcoming-orders' });
      return;
    }
    if (
      subscriptionStore.selectedSubscription?.state === 'active' &&
      !scheduleStore.activeFutureWeeks
    ) {
      router.push({ name: 'pasta-plan' });
      return;
    }

    trackCustomEvent('CTA_clicked', {
      click_id: 'addresslookup_proposition_RK',
      click_text: 'ORDER NOW',
      click_url: window.location.href,
    });
    if (!address.value) {
      focusInput();
      return;
    }

    if (!basketStore.basketKey) {
      throw new Error('No basket id found');
    }

    updateRecipeKitBasketMutation.mutate(
      {
        id: basketStore.basketKey,
        params: {
          address: address.value,
        },
      },
      {
        onSuccess: () => {
          trackCustomEvent('address-lookup-recipe-kit-click');

          const query = isEligibleForPastaNow.value ? route.query : { ...route.query, dtw: 'true' };

          router.push({ name: 'product-box', query });
        },
      }
    );
  };

  const goToPickup = async () => {
    trackCustomEvent('CTA_clicked', {
      click_id: 'addresslookup_proposition_PICKUP',
      click_text: 'ORDER NOW',
      click_url: window.location.href,
    });

    router.push({
      name: 'pasta-now-find',
      query: { type: 'pickup', restaurantId: availablePickupStoreId.value, vc: voucherCode },
    });
  };

  const goToEatInRestaurant = async () => {
    if (availableEatInStore.value) {
      trackCustomEvent('CTA_clicked', {
        click_id: `addresslookup_proposition_EATIN_${availableEatInStore.value.attributes.siteSlug}`,
        click_text: 'ORDER NOW',
        click_url: window.location.href,
      });
      window.location.replace(
        `https://pastaevangelists.com/pages/pasta-evangelists-${availableEatInStore.value.attributes.siteSlug}`
      );
    } else {
      trackCustomEvent('CTA_clicked', {
        click_id: 'addresslookup_proposition_EATIN_ALL_RESTAURANTS',
        click_text: 'ORDER NOW',
        click_url: window.location.href,
      });
      window.location.replace('https://pastaevangelists.com/pages/pasta-evangelists-restaurants');
    }
  };

  const scrollToTitle = () => {
    if (window.screen.width < 768) title.value?.scrollIntoView();
  };

  const displayAddress = computed(() => {
    if (!address.value) return '';
    if (address.value.address1 === 'ignore_me') {
      return convertToShortAddressString({
        address1: '',
        address2: address.value.address2,
        city: address.value.city,
        zip: address.value.zip,
      });
    }
    return `${address.value.address1}, ${address.value.zip}`;
  });

  // Functions to make sure the address search component is always the right background image height
  const setElementHeight = (elementId: string, offset: number) => {
    const element = document.getElementById(elementId);
    if (element) element.style.height = `${document.documentElement.clientHeight - offset}px`;
  };

  const updateHeightOnResize = () => {
    screenHeight.value = window.innerHeight;
    const stagingBannerHeight = document.getElementById('staging-banner')?.clientHeight || 0;
    const marketingBannerHeight = document.getElementById('marketing-banner')?.clientHeight || 0;

    let offset = 63; // Default offset - desktop
    setElementHeight(
      'pasta-now-address-search',
      offset + stagingBannerHeight + marketingBannerHeight
    );
    if (window.innerWidth >= 768 && window.innerWidth < 1024) {
      // Tablet
      offset = 334;
      setElementHeight(
        'pn-address-search-mobile',
        offset + stagingBannerHeight + marketingBannerHeight
      );
    } else {
      offset = 271; // Default offset for mobile
      setElementHeight(
        'pn-address-search-mobile',
        offset + stagingBannerHeight + marketingBannerHeight
      );
    }
  };

  window.addEventListener('resize', updateHeightOnResize);

  watch(isStep1, () => {
    updateHeightOnResize();
  });

  watch(
    isMounted,
    () => {
      nextTick(updateHeightOnResize);
    },
    { immediate: true }
  );
</script>
